import { useStore } from '@nanostores/react';
import type { ITheme } from '@xterm/xterm';
import { useEffect, useRef, type ComponentProps } from 'react';
import { getTerminalTheme } from './theme';
import { createXTerm } from './xterm';
import type { ShellDriver } from '~/lib/shell/driver';
import { themeStore } from '~/lib/stores/theme';
import { webcontainer } from '~/lib/webcontainer';
import { useInitRef } from '~/utils/init-ref';
import { runShellInTerminal, type RunShellOptions } from '~/utils/shell';

export interface TerminalProps extends ComponentProps<'div'> {
  id: string;
  className?: string;
  readonly?: boolean;
  theme?: ITheme;
  driver?: ShellDriver;
  env?: Record<string, string | boolean>;
  onTryExit?: () => boolean;

  // init-only prop
  webLinks?: boolean;
}

const terminals = new Map<string, ReturnType<typeof createXTerm>>([]);

function WebContainerTerminal({ driver, env, readonly, webLinks, theme, onTryExit, ...terminalProps }: TerminalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const colorTheme = useStore(themeStore);

  const abortRef = useInitRef(() => new AbortController());
  const closeableRef = useRef(onTryExit);

  useEffect(() => {
    closeableRef.current = onTryExit;
  });

  useEffect(() => {
    let unmounted = false;

    if (!terminals.has(terminalProps.id)) {
      const terminal = createXTerm({
        readonly,
        webLinks,
      });

      terminals.set(terminalProps.id, terminal);

      // if we have children, we don't start a shell
      (async () => {
        const instance = await webcontainer;

        if (unmounted) {
          // do nothing if the terminal was unmounted while we were waiting for the webcontainer
          return;
        }

        const options: RunShellOptions = {
          signal: abortRef.current.signal,
          env,
        };

        if (closeableRef.current) {
          options.shouldClose = () => {
            const isCloseable = closeableRef.current?.() ?? false;

            if (isCloseable) {
              terminals.delete(terminalProps.id);
            }

            return isCloseable;
          };
        }

        const shell = runShellInTerminal(instance, terminal.terminal, options);

        if (driver) {
          await shell.ready();

          driver.start(shell, terminal.terminal);
        }
      })();
    }

    const terminal = terminals.get(terminalProps.id)!;

    if (ref.current) {
      terminal.attach(ref.current);
    }

    return () => {
      unmounted = true;
    };
  }, [terminalProps.id]);

  useEffect(() => {
    const xterm = terminals.get(terminalProps.id);

    if (!xterm) {
      return;
    }

    /**
     * We use `requestAnimationFrame` to ensure proper timing. This hook triggers
     * before the shared layout component's hook, which updates the theme attribute
     * on the html element. Without requestAnimationFrame, the CSS variables wouldn't
     * be updated yet, as the attribute change hasn't been processed.
     *
     * By using `requestAnimationFrame`, we allow the browser to complete the attribute
     * update and recalculate styles before applying the terminal theme, ensuring that
     * all necessary CSS variables are available and correctly set when we update the
     * terminal's appearance.
     */
    requestAnimationFrame(() => {
      xterm.terminal.options.theme = getTerminalTheme({
        // we render a transparent cursor in case the terminal is readonly
        ...(readonly && { cursor: '#00000000' }),
        ...theme,
      });
    });

    xterm.terminal.options.disableStdin = readonly;
  }, [terminalProps.id, colorTheme, theme, readonly]);

  return <div translate="no" ref={ref} {...terminalProps} />;
}

type BoltTerminalProps = Omit<TerminalProps, 'env' | 'onTryExit'> & { driver: ShellDriver };

export function BoltTerminal(props: BoltTerminalProps) {
  return <WebContainerTerminal {...props} env={{ npm_config_yes: true }} />;
}

type ShellTerminalProps = Omit<TerminalProps, 'env' | 'driver'>;

export function ShellTerminal(props: ShellTerminalProps) {
  return <WebContainerTerminal {...props} />;
}
